import React, { useState } from 'react';
import './BoxPlotAnalysis.css';
import StandardizedBoxPlotComparison from './StandardizedBoxPlotComparison';
import ClassComparisonStandardized from './ClassComparisonStandardized';

const BoxPlotAnalysis = ({ data, selectedView, onViewChange, className }) => {
  const [hoveredSubject, setHoveredSubject] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);

  const viewOptions = [
    { id: 'subject-overview', name: '科目概覽 Subject Overview', description: '所有科目的成績分布比較' },
    { id: 'standardized-comparison', name: '標準化比較 Standardized Comparison', description: '水平排列的標準化盒鬚圖比較' },
    { id: 'class-comparison', name: '班級比較 Class Comparison', description: '多維度班級成績比較分析' },
    { id: 'individual-subjects', name: '個別科目 Individual Subjects', description: '每個科目的詳細分布分析' }
  ];

  // Available subjects based on the grade report
  const availableSubjects = [
    { id: 'chinese', name: '中文', color: '#ef4444' },
    { id: 'english', name: '英文', color: '#3b82f6' },
    { id: 'mathematics', name: '數學', color: '#eab308' },
    { id: 'integrated_science', name: '綜合科學', color: '#10b981' },
    { id: 'history', name: '歷史', color: '#8b5cf6' },
    { id: 'geography', name: '地理', color: '#f97316' },
    { id: 'computer', name: '電腦', color: '#06b6d4' },
    { id: 'visual_arts', name: '視覺藝術', color: '#ec4899' },
    { id: 'music', name: '音樂', color: '#84cc16' },
    { id: 'pe', name: '體育', color: '#a855f7' }
  ];

  // Initialize selected subjects with all available subjects by default
  React.useEffect(() => {
    if (data?.subjectOverview) {
      // Clear any existing selections and select all subjects by default
      const allSubjects = data.subjectOverview.map(subject => subject.subject);
      setSelectedSubjects(allSubjects);
    }
  }, [data?.subjectOverview]);

  // Debug: Log the data structure to understand subject naming
  React.useEffect(() => {
    if (data?.subjectOverview) {
      console.log('BoxPlotAnalysis - Data structure:', {
        subjectOverview: data.subjectOverview.map(s => ({
          subject: s.subject,
          hasStats: !!s.stats,
          hasData: !!s.data,
          dataLength: s.data?.length
        })),
        availableSubjects: availableSubjects.map(s => s.name)
      });
    }
  }, [data]);

  const handleSubjectToggle = (subjectName) => {
    console.log('BoxPlotAnalysis - Toggle subject:', subjectName);
    console.log('BoxPlotAnalysis - Before toggle:', selectedSubjects);
    
    setSelectedSubjects(prev => {
      const newSelection = prev.includes(subjectName) 
        ? prev.filter(s => s !== subjectName)
        : [...prev, subjectName];
      
      console.log('BoxPlotAnalysis - After toggle:', newSelection);
      return newSelection;
    });
  };

  const toggleAllSubjects = () => {
    console.log('BoxPlotAnalysis - Toggle all button clicked');
    console.log('BoxPlotAnalysis - Current selected length:', selectedSubjects.length);
    console.log('BoxPlotAnalysis - Data overview length:', data?.subjectOverview?.length);
    
    if (selectedSubjects.length === (data?.subjectOverview?.length || 0)) {
      console.log('BoxPlotAnalysis - Deselecting all subjects');
      setSelectedSubjects([]);
    } else {
      // Select all subjects from the data, not from availableSubjects
      const allSubjects = data?.subjectOverview?.map(subject => subject.subject) || [];
      console.log('BoxPlotAnalysis - Selecting all subjects:', allSubjects);
      setSelectedSubjects(allSubjects);
    }
  };

  const renderBoxPlot = (stats, subjectName, width = 200, height = 120) => {
    if (!stats) return <div className="no-data">無數據 No Data</div>;

    const { min, q1, median, q3, max, outliers, mean } = stats;
    
    // Scale factors for visualization
    const scale = height / (max - min);
    const boxY = height - (median * scale);
    
    const boxTop = height - (q3 * scale);
    const boxBottom = height - (q1 * scale);
    
    const whiskerTop = height - (max * scale);
    const whiskerBottom = height - (min * scale);

    return (
      <div className="box-plot-container">
        <svg width={width} height={height} className="box-plot-svg">
          {/* Background grid */}
          <defs>
            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f0f0f0" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
          
          {/* Whiskers */}
          <line 
            x1={width/2} y1={whiskerTop} 
            x2={width/2} y2={whiskerBottom} 
            stroke="#666" strokeWidth="2"
          />
          
          {/* Box */}
          <rect 
            x={width/2 - 25} y={boxTop} 
            width={50} height={boxBottom - boxTop} 
            fill="#4f46e5" fillOpacity="0.8" 
            stroke="#3730a3" strokeWidth="2"
          />
          
          {/* Median line */}
          <line 
            x1={width/2 - 25} y1={boxY} 
            x2={width/2 + 25} y2={boxY} 
            stroke="#ffffff" strokeWidth="3"
          />
          
          {/* Mean marker */}
          <circle 
            cx={width/2} cy={height - (mean * scale)} 
            r="4" fill="#ef4444" stroke="#dc2626" strokeWidth="2"
          />
          
          {/* Outliers */}
          {outliers.map((outlier, index) => (
            <circle 
              key={index}
              cx={width/2 + (Math.random() - 0.5) * 40} 
              cy={height - (outlier * scale)} 
              r="3" fill="#f59e0b" stroke="#d97706" strokeWidth="1"
            />
          ))}
          
          {/* Labels */}
          <text x={width/2} y={height + 15} textAnchor="middle" className="subject-label">
            {subjectName}
          </text>
        </svg>
        
        {/* Statistics display */}
        <div className="box-plot-stats">
          <div className="stat-item">
            <span className="stat-label">Q1:</span>
            <span className="stat-value">{q1.toFixed(1)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">中位數:</span>
            <span className="stat-value">{median.toFixed(1)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Q3:</span>
            <span className="stat-value">{q3.toFixed(1)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">平均:</span>
            <span className="stat-value">{mean.toFixed(1)}</span>
          </div>
        </div>
      </div>
    );
  };

  // New horizontal box plot renderer for multi-subject comparison
  const renderHorizontalBoxPlot = (stats, subjectName, width = 120, height = 200) => {
    if (!stats) return <div className="no-data">無數據 No Data</div>;

    const { min, q1, median, q3, max, outliers, mean } = stats;
    
    // Scale factors for horizontal visualization
    const scale = width / (max - min);
    const boxX = (median - min) * scale;
    
    const boxLeft = (q1 - min) * scale;
    const boxRight = (q3 - min) * scale;
    
    const whiskerLeft = (min - min) * scale;
    const whiskerRight = (max - min) * scale;

    // Get consistent color for each subject
    const subjectColor = availableSubjects.find(s => s.name === subjectName)?.color || '#4f46e5';

    return (
      <div className="horizontal-box-plot-container">
        <svg width={width} height={height} className="horizontal-box-plot-svg">
          {/* Background grid */}
          <defs>
            <pattern id="hgrid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f0f0f0" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hgrid)" />
          
          {/* Whiskers */}
          <line 
            x1={whiskerLeft} y1={height/2} 
            x2={whiskerRight} y2={height/2} 
            stroke="#666" strokeWidth="2"
          />
          
          {/* Box */}
          <rect 
            x={boxLeft} y={height/2 - 20} 
            width={boxRight - boxLeft} height={40} 
            fill={subjectColor} fillOpacity="0.8" 
            stroke="#374151" strokeWidth="2"
          />
          
          {/* Median line */}
          <line 
            x1={boxX} y1={height/2 - 20} 
            x2={boxX} y2={height/2 + 20} 
            stroke="#ffffff" strokeWidth="3"
          />
          
          {/* Mean marker */}
          <circle 
            cx={(mean - min) * scale} cy={height/2} 
            r="4" fill="#ffffff" stroke="#374151" strokeWidth="2"
          />
          
          {/* Outliers */}
          {outliers.map((outlier, index) => (
            <circle 
              key={index}
              cx={(outlier - min) * scale} 
              cy={height/2 + (Math.random() - 0.5) * 60} 
              r="3" fill="#f59e0b" stroke="#d97706" strokeWidth="1"
            />
          ))}
        </svg>
      </div>
    );
  };

  const renderSubjectOverview = () => {
    if (!data?.subjectOverview) return <div className="no-data">載入中... Loading...</div>;

    console.log('BoxPlotAnalysis - Subject Overview Data:', data.subjectOverview);
    console.log('BoxPlotAnalysis - Raw subjects:', data.subjectOverview.map(s => ({
      subject: s.subject,
      hasStats: !!s.stats,
      hasData: !!s.data,
      dataLength: s.data?.length,
      statsKeys: s.stats ? Object.keys(s.stats) : null
    })));
    console.log('BoxPlotAnalysis - Selected subjects:', selectedSubjects);
    console.log('BoxPlotAnalysis - Available subjects:', availableSubjects);

    // Filter subjects based on selection and show those with data
    const validSubjects = data.subjectOverview
      .filter(subject => selectedSubjects.includes(subject.subject))
      .filter(subject => subject.stats && subject.data && subject.data.length > 0);

    console.log('BoxPlotAnalysis - Valid subjects:', validSubjects.length);
    console.log('BoxPlotAnalysis - Valid subjects details:', validSubjects.map(s => ({
      subject: s.subject,
      dataLength: s.data.length,
      stats: s.stats
    })));

    // Always show the subject selector, even when no data is available

    return (
      <div className="subject-overview">
        <h3>科目成績分布比較 Subject Performance Distribution</h3>
        <p className="description">
          顯示各科目的成績分布情況，包括中位數、四分位數範圍、異常值等統計信息
        </p>
        
        {/* Subject Selection */}
        <div className="subject-selector-container">
          <div className="subject-selector-header">
            <h4>選擇要比較的科目 Select Subjects to Compare</h4>
            <button 
              className="toggle-all-button"
              onClick={toggleAllSubjects}
            >
              {selectedSubjects.length === (data?.subjectOverview?.length || 0) ? '取消全選' : '全選'}
            </button>
          </div>
          <div className="subject-checkboxes">
            {data?.subjectOverview?.map((subject, index) => (
              <label key={index} className="subject-checkbox">
                <input
                  type="checkbox"
                  checked={selectedSubjects.includes(subject.subject)}
                  onChange={() => handleSubjectToggle(subject.subject)}
                />
                <span 
                  className="subject-color-indicator" 
                  style={{ backgroundColor: availableSubjects.find(s => s.name === subject.subject)?.color || '#4f46e5' }}
                ></span>
                <span className="subject-name">{subject.subject}</span>
              </label>
              ))}
          </div>
        </div>

        {/* Show data or no-data message */}
        {validSubjects.length === 0 ? (
          <div className="no-data">
            <p>暫無有效的科目數據 No Valid Subject Data</p>
            <p className="debug-info">
              已選擇科目: {selectedSubjects.join(', ')} | 
              總科目數: {data.subjectOverview.length} | 
              有效科目數: {data.subjectOverview.filter(s => s.stats && s.data && s.data.length > 0).length}
            </p>
            <div className="debug-details" style={{marginTop: '20px', padding: '10px', background: '#f0f0f0', borderRadius: '8px'}}>
              <h4>Debug Information:</h4>
              <pre style={{fontSize: '12px', overflow: 'auto', maxHeight: '200px'}}>
                {JSON.stringify({
                  subjectOverview: data.subjectOverview.map(s => ({
                    subject: s.subject,
                    dataLength: s.data?.length || 0,
                    hasStats: !!s.stats,
                    sampleData: s.data?.slice(0, 3) || []
                  }))
                }, null, 2)}
              </pre>
            </div>
          </div>
        ) : (
          <>
            {/* Horizontal Multi-Subject Box Plot */}
            <div className="horizontal-box-plot-container">
              <div className="horizontal-box-plot">
                {validSubjects.map((subject, index) => (
                  <div key={index} className="horizontal-box-plot-item">
                    <div className="box-plot-label">{subject.subject}</div>
                    {renderHorizontalBoxPlot(subject.stats, subject.subject, 120, 200)}
                    <div className="box-plot-stats-horizontal">
                      <div className="stat-item-horizontal">
                        <span className="stat-label">Q1:</span>
                        <span className="stat-value">{subject.stats.q1.toFixed(1)}</span>
                      </div>
                      <div className="stat-item-horizontal">
                        <span className="stat-label">中位數:</span>
                        <span className="stat-value">{subject.stats.median.toFixed(1)}</span>
                      </div>
                      <div className="stat-item-horizontal">
                        <span className="stat-label">Q3:</span>
                        <span className="stat-value">{subject.stats.q3.toFixed(1)}</span>
                      </div>
                      <div className="stat-item-horizontal">
                        <span className="stat-label">平均:</span>
                        <span className="stat-value">{subject.stats.mean.toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* View Toggle (Alternative) */}
            <div className="view-toggle">
              <button 
                className="toggle-button active"
                onClick={() => onViewChange('subject-overview')}
              >
                水平比較 Horizontal Comparison
              </button>
              <button 
                className="toggle-button"
                onClick={() => onViewChange('subject-overview-grid')}
              >
                網格視圖 Grid View
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  // Grid view alternative for subject overview
  const renderSubjectOverviewGrid = () => {
    if (!data?.subjectOverview) return <div className="no-data">載入中... Loading...</div>;

    // Filter subjects based on selection and show those with data
    const validSubjects = data.subjectOverview
      .filter(subject => selectedSubjects.includes(subject.subject))
      .filter(subject => subject.stats && subject.data && subject.data.length > 0);

    if (validSubjects.length === 0) {
      return (
        <div className="subject-overview">
          <h3>科目成績分布比較 Subject Performance Distribution</h3>
          <p className="description">
            顯示各科目的成績分布情況，包括中位數、四分位數範圍、異常值等統計信息
          </p>
          <div className="no-data">暫無有效的科目數據 No Valid Subject Data</div>
        </div>
      );
    }

    return (
      <div className="subject-overview">
        <h3>科目成績分布比較 Subject Performance Distribution</h3>
        <p className="description">
          顯示各科目的成績分布情況，包括中位數、四分位數範圍、異常值等統計信息
        </p>
        
        <div className="box-plots-grid">
          {validSubjects.map((subject, index) => (
            <div 
              key={index} 
              className="box-plot-item"
              onMouseEnter={() => setHoveredSubject(subject.subject)}
              onMouseLeave={() => setHoveredSubject(null)}
            >
              {renderBoxPlot(subject.stats, subject.subject, 180, 140)}
              <div className="subject-info">
                <h4>{subject.subject}</h4>
                <p>學生人數: {subject.data.length}</p>
                {hoveredSubject === subject.subject && subject.stats && (
                  <div className="hover-details">
                    <p>範圍: {subject.stats.min.toFixed(1)} - {subject.stats.max.toFixed(1)}</p>
                    <p>IQR: {(subject.stats.q3 - subject.stats.q1).toFixed(1)}</p>
                    <p>異常值: {subject.stats.outliers.length}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderClassComparison = () => {
    if (!data?.classComparison) return <div className="no-data">載入中... Loading...</div>;

    return (
      <div className="class-comparison">
        <h3>班級整體表現比較 Class Overall Performance</h3>
        <p className="description">
          比較不同班級的整體學業表現，幫助識別班級間的差異
        </p>
        
        <div className="class-box-plots">
          {Object.entries(data.classComparison).map(([key, classData]) => (
            <div key={key} className="class-box-plot">
              <h4>{classData.name}</h4>
              {renderBoxPlot(
                calculateBoxPlotStats(classData.data), 
                `Class ${classData.name}`, 
                250, 
                160
              )}
            </div>
          ))}
        </div>
        
        <div className="comparison-insights">
          <h4>分析洞察 Analysis Insights</h4>
          <ul>
            <li>中位數位置反映班級整體水平</li>
            <li>四分位數範圍顯示成績的集中程度</li>
            <li>異常值可能表示需要特別關注的學生</li>
            <li>平均數與中位數的差異反映分布的偏斜程度</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderIndividualSubjects = () => {
    if (!data?.individualSubjects) return <div className="no-data">載入中... Loading...</div>;

    // Filter out subjects with no valid stats
    const validSubjects = data.individualSubjects.filter(subject => subject.stats && subject.data && subject.data.length > 0);

    if (validSubjects.length === 0) {
      return (
        <div className="individual-subjects">
          <h3>個別科目詳細分析 Individual Subject Analysis</h3>
          <p className="description">
            深入分析每個科目的成績分布，識別學習強項和需要改進的領域
          </p>
          <div className="no-data">暫無有效的科目數據 No Valid Subject Data</div>
        </div>
      );
    }

    return (
      <div className="individual-subjects">
        <h3>個別科目詳細分析 Individual Subject Analysis</h3>
        <p className="description">
          深入分析每個科目的成績分布，識別學習強項和需要改進的領域
        </p>
        
        <div className="subject-detail-grid">
          {validSubjects.map((subject, index) => (
            <div key={index} className="subject-detail-item">
              <h4>{subject.subject}</h4>
              {renderBoxPlot(subject.stats, subject.subject, 220, 150)}
              
              <div className="subject-analysis">
                <div className="performance-indicators">
                  <div className="indicator">
                    <span className="indicator-label">優秀 (A):</span>
                    <span className="indicator-value">
                      {subject.data.filter(score => score >= 80).length} 
                      ({((subject.data.filter(score => score >= 80).length / subject.data.length) * 100).toFixed(1)}%)
                    </span>
                  </div>
                  <div className="indicator">
                    <span className="indicator-label">良好 (B):</span>
                    <span className="indicator-value">
                      {subject.data.filter(score => score >= 70 && score < 80).length}
                      ({((subject.data.filter(score => score >= 70 && score < 80).length / subject.data.length) * 100).toFixed(1)}%)
                    </span>
                  </div>
                  <div className="indicator">
                    <span className="indicator-label">需改進:</span>
                    <span className="indicator-value">
                      {subject.data.filter(score => score < 70).length}
                      ({((subject.data.filter(score => score < 70).length / subject.data.length) * 100).toFixed(1)}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const calculateBoxPlotStats = (data) => {
    if (!data || data.length === 0) return null;
    
    // Filter out invalid values (null, undefined, NaN)
    const validData = data.filter(x => x !== null && x !== undefined && !isNaN(x));
    
    if (validData.length === 0) return null;
    
    const sorted = [...validData].sort((a, b) => a - b);
    const n = sorted.length;
    
    // Handle edge case where all values are the same
    if (sorted[0] === sorted[n - 1]) {
      return {
        min: sorted[0],
        q1: sorted[0],
        median: sorted[0],
        q3: sorted[0],
        max: sorted[0],
        outliers: [],
        mean: sorted[0]
      };
    }
    
    const q1 = sorted[Math.floor(n * 0.25)];
    const q2 = sorted[Math.floor(n * 0.5)];
    const q3 = sorted[Math.floor(n * 0.75)];
    
    const iqr = q3 - q1;
    const lowerBound = q1 - 1.5 * iqr;
    const upperBound = q3 + 1.5 * iqr;
    
    const outliers = sorted.filter(x => x < lowerBound || x > upperBound);
    const min = Math.max(sorted[0], lowerBound);
    const max = Math.min(sorted[n - 1], upperBound);
    
    return {
      min,
      q1,
      median: q2,
      q3,
      max,
      outliers,
      mean: validData.reduce((sum, x) => sum + x, 0) / validData.length
    };
  };

  if (!data) {
    return (
      <div className="box-plot-analysis">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>載入盒鬚圖數據中... Loading Box Plot Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="box-plot-analysis">
      <div className="analysis-header">
        <h2>盒鬚圖分析 Box Plot Analysis</h2>
        <p className="class-info">班級: {className}</p>
      </div>

      <div className="view-selector">
        {viewOptions.map(option => (
          <button
            key={option.id}
            className={selectedView === option.id ? 'active' : ''}
            onClick={() => onViewChange(option.id)}
          >
            <div className="option-name">{option.name}</div>
            <div className="option-description">{option.description}</div>
          </button>
        ))}
      </div>

      <div className="analysis-content">
        {selectedView === 'subject-overview' && renderSubjectOverview()}
        {selectedView === 'subject-overview-grid' && renderSubjectOverviewGrid()}
        {selectedView === 'standardized-comparison' && (
          <StandardizedBoxPlotComparison data={data} className={className} />
        )}
        {selectedView === 'class-comparison' && (
          <ClassComparisonStandardized data={data} className={className} />
        )}
        {selectedView === 'individual-subjects' && renderIndividualSubjects()}
      </div>

      <div className="box-plot-legend">
        <h4>圖例說明 Legend</h4>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-color box-color"></div>
            <span>四分位數範圍 (Q1-Q3)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color median-color"></div>
            <span>中位數 (Median)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color mean-color"></div>
            <span>平均數 (Mean)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color outlier-color"></div>
            <span>異常值 (Outliers)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color whisker-color"></div>
            <span>鬚線範圍 (Whisker Range)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoxPlotAnalysis;
